/**
 * Upload DICOM Modal
 *
 * Modal for uploading DICOM files with automatic metadata extraction.
 * Supports:
 * - Single or multiple DICOM file uploads
 * - Patient selection (existing) or patient creation (automatic match/create)
 * - Metadata extraction from DICOM headers
 */

import { useState, useRef, useCallback } from 'react'
import { Loader2, Upload, FileImage, AlertCircle, CheckCircle, X, UserPlus, Search, FolderOpen } from 'lucide-react'
import * as dicomParser from 'dicom-parser'

import { usePatients } from '@/application/patients'
import { useUploadStudy, useUploadStudyBatch } from '@/application/studies'
import { useActiveModalities } from '@/application/modalities'
import type { CreateStudyData } from '@/domain/study/types'
import { AttachmentUploader, prepareAttachmentsForUpload, type AttachmentFile } from './AttachmentUploader'
import { PatientDataForm } from './PatientDataForm'
import type { Patient } from '@/domain/patient/types'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import { BODY_PART_LABELS } from '@/domain/study/types'
import {
  extractPatientDataFromDicom,
  isValidPatientData,
  type PatientCreateData,
} from '@/lib/dicom-helpers'

// =============================================================================
// Types
// =============================================================================

interface UploadDicomModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

interface DicomMetadata {
  studyInstanceUID: string
  accessionNumber?: string
  modality?: string
  studyDate?: string
  studyTime?: string
  studyDescription?: string
  referringPhysician?: string
  performingPhysician?: string
  bodyPartExamined?: string
  institutionName?: string
  // Patient data from DICOM
  patientName?: string
  patientId?: string
  patientBirthDate?: string
  patientSex?: string
  // Instance data
  sopInstanceUID?: string
  seriesInstanceUID?: string
  instanceNumber?: number
}

interface ParsedDicomFile {
  file: File
  metadata: DicomMetadata
  base64Data: string
  isValid: boolean
  error?: string
}

type PatientMode = 'select' | 'create'

// =============================================================================
// DICOM Parser Helpers
// =============================================================================

/**
 * Parse DICOM date format (YYYYMMDD) to ISO date (YYYY-MM-DD)
 */
function parseDicomDate(dicomDate: string | undefined): string | undefined {
  if (!dicomDate || dicomDate.length < 8) return undefined
  const year = dicomDate.slice(0, 4)
  const month = dicomDate.slice(4, 6)
  const day = dicomDate.slice(6, 8)
  return `${year}-${month}-${day}`
}

/**
 * Parse DICOM time format (HHmmss.ffffff) to HH:mm:ss
 */
function parseDicomTime(dicomTime: string | undefined): string | undefined {
  if (!dicomTime || dicomTime.length < 4) return undefined
  const hours = dicomTime.slice(0, 2)
  const minutes = dicomTime.slice(2, 4)
  const seconds = dicomTime.length >= 6 ? dicomTime.slice(4, 6) : '00'
  return `${hours}:${minutes}:${seconds}`
}

/**
 * Get string value from DICOM dataset
 */
function getDicomString(dataSet: dicomParser.DataSet, tag: string): string | undefined {
  try {
    const element = dataSet.elements[tag]
    if (!element) return undefined
    const value = dataSet.string(tag)
    return value?.trim() || undefined
  } catch {
    return undefined
  }
}

/**
 * Get integer value from DICOM dataset
 */
function getDicomInt(dataSet: dicomParser.DataSet, tag: string): number | undefined {
  try {
    const value = dataSet.intString(tag)
    return value !== undefined ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * Parse DICOM file and extract metadata
 */
async function parseDicomFile(file: File): Promise<ParsedDicomFile> {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer
        const byteArray = new Uint8Array(arrayBuffer)

        // Parse DICOM
        const dataSet = dicomParser.parseDicom(byteArray)

        // Extract metadata using DICOM tags
        const metadata: DicomMetadata = {
          // Study Instance UID (0020,000D) - Required
          studyInstanceUID: getDicomString(dataSet, 'x0020000d') || generateStudyInstanceUID(),
          // Accession Number (0008,0050)
          accessionNumber: getDicomString(dataSet, 'x00080050'),
          // Modality (0008,0060)
          modality: getDicomString(dataSet, 'x00080060'),
          // Study Date (0008,0020)
          studyDate: parseDicomDate(getDicomString(dataSet, 'x00080020')),
          // Study Time (0008,0030)
          studyTime: parseDicomTime(getDicomString(dataSet, 'x00080030')),
          // Study Description (0008,1030)
          studyDescription: getDicomString(dataSet, 'x00081030'),
          // Referring Physician (0008,0090)
          referringPhysician: getDicomString(dataSet, 'x00080090'),
          // Performing Physician (0008,1050)
          performingPhysician: getDicomString(dataSet, 'x00081050'),
          // Body Part Examined (0018,0015)
          bodyPartExamined: getDicomString(dataSet, 'x00180015'),
          // Institution Name (0008,0080)
          institutionName: getDicomString(dataSet, 'x00080080'),
          // Patient Name (0010,0010)
          patientName: getDicomString(dataSet, 'x00100010'),
          // Patient ID (0010,0020)
          patientId: getDicomString(dataSet, 'x00100020'),
          // Patient Birth Date (0010,0030)
          patientBirthDate: getDicomString(dataSet, 'x00100030'),
          // Patient Sex (0010,0040)
          patientSex: getDicomString(dataSet, 'x00100040'),
          // SOP Instance UID (0008,0018)
          sopInstanceUID: getDicomString(dataSet, 'x00080018'),
          // Series Instance UID (0020,000E)
          seriesInstanceUID: getDicomString(dataSet, 'x0020000e'),
          // Instance Number (0020,0013)
          instanceNumber: getDicomInt(dataSet, 'x00200013'),
        }

        // Convert to base64
        const base64Data = arrayBufferToBase64(arrayBuffer)

        resolve({
          file,
          metadata,
          base64Data,
          isValid: true,
        })
      } catch (error) {
        resolve({
          file,
          metadata: { studyInstanceUID: generateStudyInstanceUID() },
          base64Data: '',
          isValid: false,
          error: error instanceof Error ? error.message : 'Error al parsear archivo DICOM',
        })
      }
    }

    reader.onerror = () => {
      resolve({
        file,
        metadata: { studyInstanceUID: generateStudyInstanceUID() },
        base64Data: '',
        isValid: false,
        error: 'Error al leer archivo',
      })
    }

    reader.readAsArrayBuffer(file)
  })
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Generate a unique Study Instance UID
 */
function generateStudyInstanceUID(): string {
  const root = '1.2.840.113619.2' // OID root
  const timestamp = Date.now()
  const random = Math.floor(Math.random() * 1000000)
  return `${root}.${timestamp}.${random}`
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// =============================================================================
// Component
// =============================================================================

export function UploadDicomModal({
  open,
  onOpenChange,
  onSuccess,
}: UploadDicomModalProps) {
  // State
  const [selectedFiles, setSelectedFiles] = useState<ParsedDicomFile[]>([])
  const [isParsingFiles, setIsParsingFiles] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [patientMode, setPatientMode] = useState<PatientMode>('select')
  const [selectedPatientId, setSelectedPatientId] = useState<string>('')
  const [patientSearch, setPatientSearch] = useState<string>('')
  const [patientData, setPatientData] = useState<PatientCreateData | null>(null)
  const [formData, setFormData] = useState<Partial<DicomMetadata>>({})
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [attachments, setAttachments] = useState<AttachmentFile[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)

  // Queries
  const { data: patientsData, isLoading: isLoadingPatients } = usePatients({
    name: patientSearch || undefined,
    limit: 20,
  })
  const uploadMutation = useUploadStudy()
  const uploadBatchMutation = useUploadStudyBatch()

  const patients = patientsData?.items || []
  const isUploading = uploadMutation.isPending || uploadBatchMutation.isPending

  /**
   * Check if a file is a DICOM file by extension or lack of extension
   */
  const isDicomFile = (file: File): boolean => {
    const name = file.name.toLowerCase()
    const dicomExtensions = ['.dcm', '.dicom', '.dic']

    // Check for DICOM extensions
    if (dicomExtensions.some(ext => name.endsWith(ext))) return true

    // Files without extension are often DICOM
    if (!name.includes('.') || name.endsWith('.')) return true

    // Check MIME type
    if (file.type === 'application/dicom') return true

    return false
  }

  /**
   * Recursively get all files from a directory entry
   */
  const getFilesFromEntry = async (entry: FileSystemEntry): Promise<File[]> => {
    if (entry.isFile) {
      return new Promise((resolve) => {
        (entry as FileSystemFileEntry).file(
          (file) => resolve([file]),
          () => resolve([])
        )
      })
    } else if (entry.isDirectory) {
      const dirReader = (entry as FileSystemDirectoryEntry).createReader()
      const entries = await new Promise<FileSystemEntry[]>((resolve) => {
        const allEntries: FileSystemEntry[] = []
        const readEntries = () => {
          dirReader.readEntries((entries) => {
            if (entries.length === 0) {
              resolve(allEntries)
            } else {
              allEntries.push(...entries)
              readEntries()
            }
          }, () => resolve(allEntries))
        }
        readEntries()
      })
      const files = await Promise.all(entries.map(getFilesFromEntry))
      return files.flat()
    }
    return []
  }

  /**
   * Handle drag and drop
   */
  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const items = e.dataTransfer.items
    if (!items || items.length === 0) return

    setIsParsingFiles(true)
    setUploadError(null)

    try {
      // Get all files from dropped items (including folders)
      const allFiles: File[] = []

      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const entry = item.webkitGetAsEntry()
        if (entry) {
          const files = await getFilesFromEntry(entry)
          allFiles.push(...files)
        }
      }

      if (allFiles.length === 0) {
        setUploadError('No se encontraron archivos')
        return
      }

      // Filter potential DICOM files
      const potentialDicomFiles = allFiles.filter(isDicomFile)

      if (potentialDicomFiles.length === 0) {
        setUploadError('No se encontraron archivos DICOM en la seleccion')
        return
      }

      // Parse all potential DICOM files
      const parsed = await Promise.all(potentialDicomFiles.map(parseDicomFile))
      const newValidFiles = parsed.filter((f) => f.isValid)

      if (newValidFiles.length === 0) {
        setUploadError('Ningun archivo DICOM valido encontrado')
        return
      }

      // Get existing Study Instance UID if we have files already
      const existingStudyUID = selectedFiles.length > 0
        ? selectedFiles[0].metadata.studyInstanceUID
        : null

      // Verify all new files have the same Study Instance UID
      const newStudyUIDs = new Set(newValidFiles.map((f) => f.metadata.studyInstanceUID))
      if (newStudyUIDs.size > 1) {
        setUploadError(`Se encontraron ${newStudyUIDs.size} estudios diferentes. Todos los archivos deben ser del mismo estudio.`)
        return
      }

      const newStudyUID = newValidFiles[0].metadata.studyInstanceUID

      // If we already have files, verify new files match the existing study
      if (existingStudyUID && newStudyUID !== existingStudyUID) {
        setUploadError('Los nuevos archivos deben ser del mismo estudio que los ya seleccionados')
        return
      }

      // Filter out duplicates
      const existingSOPUIDs = new Set(
        selectedFiles.map((f) => f.metadata.sopInstanceUID).filter(Boolean)
      )
      const existingFileNames = new Set(selectedFiles.map((f) => f.file.name))

      const uniqueNewFiles = newValidFiles.filter((f) => {
        if (f.metadata.sopInstanceUID && existingSOPUIDs.has(f.metadata.sopInstanceUID)) {
          return false
        }
        return !existingFileNames.has(f.file.name)
      })

      if (uniqueNewFiles.length === 0) {
        setUploadError('Los archivos seleccionados ya estan en la lista')
        return
      }

      // Merge with existing files
      const mergedFiles = [...selectedFiles, ...uniqueNewFiles]
      setSelectedFiles(mergedFiles)

      // Use metadata from first file (only if no files were selected before)
      if (selectedFiles.length === 0) {
        const firstFile = newValidFiles[0]
        setFormData(firstFile.metadata)

        if (firstFile.metadata.patientId || firstFile.metadata.patientName) {
          const extractedPatientData = extractPatientDataFromDicom({
            patientId: firstFile.metadata.patientId,
            patientName: firstFile.metadata.patientName,
            patientBirthDate: firstFile.metadata.patientBirthDate,
            patientSex: firstFile.metadata.patientSex,
          })
          setPatientData(extractedPatientData)
        }
      }
    } finally {
      setIsParsingFiles(false)
    }
  }, [selectedFiles])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  // Handlers
  const handleFilesSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const allFiles = Array.from(e.target.files || [])
    if (allFiles.length === 0) return

    // Filter potential DICOM files
    const potentialDicomFiles = allFiles.filter(isDicomFile)

    if (potentialDicomFiles.length === 0) {
      setUploadError('No se encontraron archivos DICOM en la seleccion')
      return
    }

    setUploadError(null)
    setIsParsingFiles(true)

    try {
      // Parse all potential DICOM files
      const parsed = await Promise.all(potentialDicomFiles.map(parseDicomFile))
      const newValidFiles = parsed.filter((f) => f.isValid)

      if (newValidFiles.length === 0) {
        setUploadError('Ningun archivo DICOM valido encontrado')
        return
      }

      // Get existing Study Instance UID if we have files already
      const existingStudyUID = selectedFiles.length > 0
        ? selectedFiles[0].metadata.studyInstanceUID
        : null

      // Verify all new files have the same Study Instance UID
      const newStudyUIDs = new Set(newValidFiles.map((f) => f.metadata.studyInstanceUID))
      if (newStudyUIDs.size > 1) {
        setUploadError(`Se encontraron ${newStudyUIDs.size} estudios diferentes. Todos los archivos deben ser del mismo estudio.`)
        return
      }

      const newStudyUID = newValidFiles[0].metadata.studyInstanceUID

      // If we already have files, verify new files match the existing study
      if (existingStudyUID && newStudyUID !== existingStudyUID) {
        setUploadError('Los nuevos archivos deben ser del mismo estudio que los ya seleccionados')
        return
      }

      // Filter out duplicates by SOP Instance UID (more reliable than filename)
      const existingSOPUIDs = new Set(
        selectedFiles
          .map((f) => f.metadata.sopInstanceUID)
          .filter(Boolean)
      )
      const existingFileNames = new Set(selectedFiles.map((f) => f.file.name))

      const uniqueNewFiles = newValidFiles.filter((f) => {
        // Check by SOP Instance UID first (if available)
        if (f.metadata.sopInstanceUID && existingSOPUIDs.has(f.metadata.sopInstanceUID)) {
          return false
        }
        // Fallback to filename
        return !existingFileNames.has(f.file.name)
      })

      if (uniqueNewFiles.length === 0) {
        setUploadError('Los archivos seleccionados ya estan en la lista')
        return
      }

      // Merge with existing files
      const mergedFiles = [...selectedFiles, ...uniqueNewFiles]
      setSelectedFiles(mergedFiles)

      // Use metadata from first file (only if no files were selected before)
      if (selectedFiles.length === 0) {
        const firstFile = newValidFiles[0]
        setFormData(firstFile.metadata)

        // Extract patient data for "create" mode
        if (firstFile.metadata.patientId || firstFile.metadata.patientName) {
          const extractedPatientData = extractPatientDataFromDicom({
            patientId: firstFile.metadata.patientId,
            patientName: firstFile.metadata.patientName,
            patientBirthDate: firstFile.metadata.patientBirthDate,
            patientSex: firstFile.metadata.patientSex,
          })
          setPatientData(extractedPatientData)
        }
      }
    } finally {
      setIsParsingFiles(false)
      // Reset file input to allow selecting the same files again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      if (folderInputRef.current) {
        folderInputRef.current.value = ''
      }
    }
  }, [selectedFiles])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setUploadError(null)

    if (selectedFiles.length === 0) {
      setUploadError('Debe seleccionar al menos un archivo DICOM')
      return
    }

    // Validate patient selection
    if (patientMode === 'select' && !selectedPatientId) {
      setUploadError('Debe seleccionar un paciente')
      return
    }

    if (patientMode === 'create' && (!patientData || !isValidPatientData(patientData))) {
      setUploadError('Debe proporcionar al menos el numero de documento o el apellido del paciente')
      return
    }

    if (!formData.modality) {
      setUploadError('La modalidad es requerida')
      return
    }

    if (!formData.studyDate) {
      setUploadError('La fecha del estudio es requerida')
      return
    }

    if (attachments.length === 0) {
      setUploadError('Debe adjuntar al menos un documento (historia clinica, orden medica, etc.)')
      return
    }

    try {
      // Prepare base study data
      const baseStudyData = {
        studyInstanceUID: formData.studyInstanceUID || selectedFiles[0].metadata.studyInstanceUID,
        accessionNumber: formData.accessionNumber,
        modality: formData.modality!,
        studyDate: formData.studyDate,
        studyTime: formData.studyTime,
        studyDescription: formData.studyDescription,
        referringPhysician: formData.referringPhysician,
        performingPhysician: formData.performingPhysician,
        bodyPartExamined: formData.bodyPartExamined,
        institutionName: formData.institutionName,
        attachments: prepareAttachmentsForUpload(attachments),
      }

      // Patient selection based on mode
      const patientSelection =
        patientMode === 'select'
          ? { patientId: selectedPatientId }
          : { patientData: patientData! }

      let result

      if (selectedFiles.length === 1) {
        // Single file upload
        const studyData: CreateStudyData = {
          ...baseStudyData,
          ...patientSelection,
          fileData: selectedFiles[0].base64Data,
          fileName: selectedFiles[0].file.name,
          contentType: 'application/dicom',
        }

        result = await uploadMutation.mutateAsync(studyData)
      } else {
        // Batch upload
        const studyData: CreateStudyData = {
          ...baseStudyData,
          ...patientSelection,
          files: selectedFiles.map((f) => ({
            fileData: f.base64Data,
            fileName: f.file.name,
            sopInstanceUID: f.metadata.sopInstanceUID,
            seriesInstanceUID: f.metadata.seriesInstanceUID,
            instanceNumber: f.metadata.instanceNumber,
            contentType: 'application/dicom',
          })),
        }

        result = await uploadBatchMutation.mutateAsync(studyData)
      }

      // Show assignment-specific toast based on result
      const { assignmentInfo, patientIsNew } = result
      let successMessage = ''

      if (patientIsNew) {
        successMessage = 'Paciente creado automaticamente. '
      }

      if (assignmentInfo.assigned) {
        if (assignmentInfo.availableRadiologistsCount === 1) {
          successMessage += `Estudio asignado a ${assignmentInfo.assignedToEmail} (unico radiologo disponible)`
        } else {
          successMessage += `Estudio asignado a ${assignmentInfo.assignedToEmail}`
        }
        toast.success(successMessage, { duration: 5000 })
      } else {
        // Show detailed error with action guidance
        const errorTitle = assignmentInfo.userMessage || 'No se pudo asignar automaticamente'
        const actionText = assignmentInfo.action || ''

        // Build informative error message with line breaks
        let errorMessage = `⚠️ ${errorTitle}`
        if (actionText) {
          errorMessage += `\n\n📋 ${actionText}`
        }
        errorMessage += '\n\n✅ El estudio fue subido y esta en la lista de trabajo para asignacion manual.'

        toast.warning(errorMessage, { duration: 12000 })
      }

      // Reset form and close
      handleClose()
      onSuccess?.()
    } catch (error) {
      // getErrorMessage saca el mensaje que redacto el servidor. Usar
      // error.message a secas muestra "Request failed with status code 400",
      // que no le dice al tecnologo que archivo esta mal ni por que.
      const errorMessage = getErrorMessage(error) || 'Error al subir el estudio'
      setUploadError(errorMessage)
      toast.error(errorMessage)
    }
  }

  const handleClose = () => {
    setSelectedFiles([])
    setIsParsingFiles(false)
    setPatientMode('select')
    setSelectedPatientId('')
    setPatientSearch('')
    setPatientData(null)
    setFormData({})
    setUploadError(null)
    setAttachments([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = ''
    }
    onOpenChange(false)
  }

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => {
      const newFiles = prev.filter((_, i) => i !== index)
      if (newFiles.length === 0) {
        setFormData({})
        setPatientData(null)
      }
      return newFiles
    })
    if (fileInputRef.current && selectedFiles.length === 1) {
      fileInputRef.current.value = ''
    }
  }

  const handleClearFiles = () => {
    setSelectedFiles([])
    setFormData({})
    setPatientData(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const updateFormData = (field: keyof DicomMetadata, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Fetch dynamic modalities from database
  const { data: modalitiesData } = useActiveModalities()

  // Options for selects - dynamic from database
  const modalityOptions =
    modalitiesData?.modalities?.map((mod) => ({
      value: mod.code,
      label: mod.name,
    })) || []

  const bodyPartOptions = Object.entries(BODY_PART_LABELS).map(([value, label]) => ({
    value,
    label,
  }))

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>Cargar Nuevo Estudio</DialogTitle>
          <DialogDescription>
            Seleccione uno o mas archivos DICOM del mismo estudio para extraer metadatos y asignar a un paciente
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Error message */}
          {uploadError && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{uploadError}</span>
            </div>
          )}

          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
              Archivos del Estudio <span className="text-red-500">*</span>
            </label>

            {/* Hidden file inputs - always available */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".dcm,.dicom,.dic,application/dicom,*"
              onChange={handleFilesSelect}
              className="hidden"
            />
            <input
              ref={folderInputRef}
              type="file"
              multiple
              // @ts-expect-error webkitdirectory is not in the standard types
              webkitdirectory=""
              directory=""
              onChange={handleFilesSelect}
              className="hidden"
            />

            {isParsingFiles ? (
              <div className="border-2 border-dashed border-[var(--primary)]/50 rounded-lg p-8 text-center bg-[var(--primary)]/5">
                <Loader2
                  className="h-10 w-10 mx-auto text-[var(--primary)] mb-3"
                  style={{ animation: 'spin 1s linear infinite' }}
                />
                <p className="text-sm font-medium text-[var(--foreground)]">
                  Procesando archivos DICOM...
                </p>
                <p className="text-xs text-[var(--foreground-muted)] mt-1">
                  Extrayendo metadatos de las imagenes
                </p>
              </div>
            ) : selectedFiles.length === 0 ? (
              <div
                className={
                  'border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ' +
                  (isDragging
                    ? 'border-[var(--primary)] bg-[var(--primary)]/10 scale-[1.02]'
                    : 'border-[var(--border)] hover:border-[var(--primary)]/50 hover:bg-[var(--background-subtle)]')
                }
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center gap-3">
                  {isDragging ? (
                    <>
                      <div className="p-4 rounded-full bg-[var(--primary)]/20">
                        <FolderOpen className="h-10 w-10 text-[var(--primary)]" />
                      </div>
                      <p className="text-base font-medium text-[var(--primary)]">
                        Suelte aqui los archivos o carpeta
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="p-4 rounded-full bg-[var(--background-subtle)]">
                        <Upload className="h-10 w-10 text-[var(--foreground-muted)]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--foreground)]">
                          Arrastre archivos o carpeta aqui
                        </p>
                        <p className="text-xs text-[var(--foreground-muted)] mt-1">
                          o haga clic para seleccionar
                        </p>
                      </div>
                      <p className="text-xs text-[var(--foreground-muted)] mt-2 px-4">
                        Los archivos DICOM seran detectados automaticamente, incluso en subcarpetas
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-[var(--foreground)]">
                    {selectedFiles.length} archivo(s) seleccionado(s)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Archivos
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => folderInputRef.current?.click()}
                    >
                      <FolderOpen className="h-3 w-3 mr-1" />
                      Carpeta
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={handleClearFiles}>
                      Limpiar
                    </Button>
                  </div>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1 border rounded-md p-2">
                  {selectedFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 p-2 bg-[var(--background-subtle)] rounded"
                    >
                      <FileImage className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm truncate flex-1">{file.file.name}</span>
                      <span className="text-xs text-[var(--foreground-muted)]">
                        {formatFileSize(file.file.size)}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleRemoveFile(idx)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                {selectedFiles[0]?.metadata.patientName && (
                  <p className="text-xs text-[var(--foreground-muted)]">
                    DICOM Patient: {selectedFiles[0].metadata.patientName}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Patient Selection - Tabs */}
          {selectedFiles.length > 0 && (
            <div className="space-y-4">
              <label className="block text-sm font-medium text-[var(--foreground)]">
                Paciente <span className="text-red-500">*</span>
              </label>

              {/* Tab buttons */}
              <div className="flex gap-2 border-b border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => setPatientMode('select')}
                  className={
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
                    (patientMode === 'select'
                      ? 'border-[var(--primary)] text-[var(--primary)]'
                      : 'border-transparent text-[var(--foreground-muted)] hover:text-[var(--foreground)]')
                  }
                >
                  <Search className="h-4 w-4" />
                  Seleccionar Existente
                </button>
                <button
                  type="button"
                  onClick={() => setPatientMode('create')}
                  className={
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
                    (patientMode === 'create'
                      ? 'border-[var(--primary)] text-[var(--primary)]'
                      : 'border-transparent text-[var(--foreground-muted)] hover:text-[var(--foreground)]')
                  }
                >
                  <UserPlus className="h-4 w-4" />
                  Crear/Buscar Automatico
                </button>
              </div>

              {/* Tab content */}
              {patientMode === 'select' ? (
                // Existing patient search
                <div>
                  <Input
                    type="text"
                    placeholder="Buscar paciente por nombre..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    className="mb-2"
                  />

                  {isLoadingPatients ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : patients.length === 0 ? (
                    <p className="text-sm text-[var(--foreground-muted)] text-center p-4">
                      {patientSearch ? 'No se encontraron pacientes' : 'Escriba para buscar pacientes'}
                    </p>
                  ) : (
                    <div className="space-y-1 max-h-32 overflow-y-auto border rounded-md p-2">
                      {patients.map((patient: Patient) => {
                        const isSelected = selectedPatientId === patient.id
                        return (
                          <button
                            key={patient.id}
                            type="button"
                            onClick={() => setSelectedPatientId(patient.id)}
                            className={
                              'w-full flex items-center justify-between p-2 rounded text-left transition-colors text-sm ' +
                              (isSelected
                                ? 'bg-[var(--primary)]/10 border border-[var(--primary)]'
                                : 'hover:bg-[var(--background-subtle)]')
                            }
                          >
                            <span>
                              {patient.firstName} {patient.lastName}
                            </span>
                            <span className="text-xs text-[var(--foreground-muted)]">
                              {patient.document.type}: {patient.document.number}
                            </span>
                            {isSelected && <CheckCircle className="h-4 w-4 text-[var(--primary)]" />}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : (
                // Patient data form (create/match automatic)
                <PatientDataForm
                  initialData={patientData}
                  onChange={setPatientData}
                  disabled={isUploading}
                />
              )}
            </div>
          )}

          {/* Metadata Fields (only show if file is selected) */}
          {selectedFiles.length > 0 && (
            <>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-[var(--foreground)] mb-3">
                  Metadatos del Estudio
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Modality */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
                      Modalidad <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={modalityOptions}
                      value={formData.modality || ''}
                      onChange={(e) => updateFormData('modality', e.target.value)}
                      placeholder="Seleccionar..."
                    />
                  </div>

                  {/* Study Date */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
                      Fecha del Estudio <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="date"
                      value={formData.studyDate || ''}
                      onChange={(e) => updateFormData('studyDate', e.target.value)}
                    />
                  </div>

                  {/* Study Description */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
                      Descripcion del Estudio
                    </label>
                    <Input
                      type="text"
                      value={formData.studyDescription || ''}
                      onChange={(e) => updateFormData('studyDescription', e.target.value)}
                      placeholder="Ej: RX Torax PA y Lateral"
                    />
                  </div>

                  {/* Body Part */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
                      Parte del Cuerpo
                    </label>
                    <Select
                      options={bodyPartOptions}
                      value={formData.bodyPartExamined || ''}
                      onChange={(e) => updateFormData('bodyPartExamined', e.target.value)}
                      placeholder="Seleccionar..."
                    />
                  </div>

                  {/* Referring Physician */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
                      Medico Remitente
                    </label>
                    <Input
                      type="text"
                      value={formData.referringPhysician || ''}
                      onChange={(e) => updateFormData('referringPhysician', e.target.value)}
                      placeholder="Dr. ..."
                    />
                  </div>

                  {/* Accession Number */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
                      Numero de Acceso
                    </label>
                    <Input
                      type="text"
                      value={formData.accessionNumber || ''}
                      onChange={(e) => updateFormData('accessionNumber', e.target.value)}
                      placeholder="ACC123456"
                    />
                  </div>

                  {/* Institution */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--foreground-muted)] mb-1">
                      Institucion
                    </label>
                    <Input
                      type="text"
                      value={formData.institutionName || ''}
                      onChange={(e) => updateFormData('institutionName', e.target.value)}
                      placeholder="Centro de Diagnostico..."
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Attachments - Always visible once file is selected */}
          {selectedFiles.length > 0 && (
            <div className="border-t pt-4">
              <AttachmentUploader
                attachments={attachments}
                onChange={setAttachments}
                minRequired={1}
                maxAllowed={10}
                maxFileSizeMB={10}
                disabled={isUploading}
              />
            </div>
          )}

          <DialogFooter className="mt-6 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                selectedFiles.length === 0 ||
                (patientMode === 'select' && !selectedPatientId) ||
                (patientMode === 'create' && (!patientData || !isValidPatientData(patientData))) ||
                attachments.length === 0 ||
                isUploading
              }
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Subir Estudio
                  {selectedFiles.length > 1 && ` (${selectedFiles.length} archivos)`}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
